import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import storeConnector from '@src/store/storeConnector';
import {
  WidgetTypeDeviceList,
  WidgetTypeMultiSectionPointList,
} from '@src/types/Widget';
import { Spinner } from 'reactstrap';
import SimplePointDataTableWithTitle from '@components/_shared/MultiColPointDataTableWithTitle';
import { generateMqttClient } from '@src/pages/_loader/initMqttClient.loader';
import { MqttClient } from 'mqtt/*';
import { useState, useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useUnitController } from '@src/contextProviders/UnitController';
import FormattedAlias from './Translations/FormattedAlias';

function WidgetDeviceRowDetails({
  widgetConfigId,
  selectedSourceDevice,
  unitControllerId: unitControllerIdProp,
  unitControllerEngineeringId: unitControllerEngineeringIdProp,
  onClose,
}: {
  widgetConfigId: WidgetTypeDeviceList;
  selectedSourceDevice: {
    name: string;
    id: string;
  };
  onClose: () => void;
  unitControllerId?: string;
  unitControllerEngineeringId?: string;
}) {
  const context = useUnitController();

  // Use props if provided, otherwise fallback to context
  const unitControllerId = unitControllerIdProp ?? context.unitControllerId;
  const unitControllerEngineeringId =
    unitControllerEngineeringIdProp ?? context.unitControllerEngineeringId;

  const {
    data: widgetConfig,
    isFetching,
    isError,
  } = useGetInternalWidgetConfig<WidgetTypeMultiSectionPointList>(
    `${widgetConfigId}DetailsModal`,
    {
      sourceDeviceId: selectedSourceDevice?.id,
      ...(unitControllerId &&
        unitControllerEngineeringId && {
          unitControllerId,
          unitControllerEngineeringId,
        }),
    },
    {
      queryKeys: [selectedSourceDevice?.id, 'deviceDetails'],
      enabled: !!widgetConfigId && !!selectedSourceDevice?.id,
    },
  );

  const [mqttClient, setMqttClient] = useState<MqttClient>();
  const mqttClientRef = useRef<MqttClient>();

  useEffect(() => {
    const startClient = async () => {
      const client = await generateMqttClient();
      mqttClientRef.current = client;
      setMqttClient(client);
    };
    startClient();
    return () => {
      mqttClientRef.current?.end();
    };
  }, []);

  return (
    <Dialog
      open={!!widgetConfigId && !!selectedSourceDevice?.id}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      {isFetching && <Spinner type='content-small' />}
      {isError && (
        <Typography textAlign='center' marginTop='50px'>
          <FormattedAlias id={'Error fetching device details.'} />
        </Typography>
      )}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {selectedSourceDevice?.name} <FormattedAlias id={'Details'} />
        <IconButton onClick={onClose} sx={{ mr: -2, mt: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box mt={1} mb={5}>
          {widgetConfig?.sections.map((sectionData, i) => (
            <SimplePointDataTableWithTitle
              key={`megapack-details-${i}`}
              sectionIndex={i}
              data={sectionData}
              useNewClient={mqttClient}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default storeConnector(WidgetDeviceRowDetails, {
  config: ['siteMeta'],
});
